// src/lib/scoring.ts
import { QUESTIONS, type Answer } from './questions'

export type SignalLevel = 'clear' | 'signalled' | 'strongly-signalled'

interface DomainScores {
  prolactin: number
  androgens: number
  insulin: number
  thyroid: number
  mood: number
  cycle: number
}

export interface ScoringResult extends DomainScores {
  totalScore: number
  signals: {
    prolactin: SignalLevel
    androgens: SignalLevel
    insulin: SignalLevel
    thyroid: SignalLevel
  }
  investigationGap: string[]
}

// Domain thresholds
const THRESHOLDS = {
  P: { max: 9,  signal: 4, strong: 6 },
  A: { max: 11, signal: 5, strong: 8 },
  I: { max: 8,  signal: 4, strong: 6 },
  T: { max: 12, signal: 5, strong: 8 },
} as const

// Question → domain mapping (shared scoring: some questions feed multiple domains)
const DOMAIN_MAP: Record<string, { domains: string[] }> = {
  Q1:  { domains: ['A', 'cycle'] },
  Q2:  { domains: ['T', 'cycle'] },
  Q3:  { domains: ['T', 'cycle'] },
  Q4:  { domains: ['cycle'] },
  Q5:  { domains: ['P', 'cycle'] },
  Q6:  { domains: ['cycle'] },
  Q7:  { domains: ['A'] },
  Q8:  { domains: ['A'] },
  Q9:  { domains: ['A', 'T'] },     // Shared: androgen + thyroid
  Q10: { domains: ['I'] },
  Q11: { domains: ['P'] },
  Q12: { domains: ['T', 'I'] },     // Shared: thyroid + insulin
  Q13: { domains: ['I'] },
  Q14: { domains: ['P'] },
  Q15: { domains: ['mood'] },
  Q16: { domains: ['T', 'mood'] },
  Q17: { domains: ['mood'] },
  Q18: { domains: ['A', 'I', 'mood'] },
  Q19: { domains: ['T', 'mood'] },
  // Q20–Q26: context questions — no clinical scores
}

export function scoreSubmission(answers: Answer[]): ScoringResult {
  const scores: DomainScores = {
    prolactin: 0, androgens: 0, insulin: 0, thyroid: 0, mood: 0, cycle: 0,
  }

  let totalScore = 0

  for (const answer of answers) {
    const question = QUESTIONS.find(q => q.id === answer.questionId)
    if (!question) continue

    const mapping = DOMAIN_MAP[answer.questionId]
    if (!mapping) continue  // Context questions (Q20–Q26)

    let answerScore: number

    if (question.type === 'multi') {
      const selectedKeys = answer.answerKey.split(',').filter(Boolean)
      answerScore = selectedKeys.reduce((sum, key) => {
        const opt = question.options.find(o => o.key === key.trim())
        return sum + (opt?.score || 0)
      }, 0)
    } else {
      const opt = question.options.find(o => o.key === answer.answerKey)
      answerScore = opt?.score || 0
    }

    totalScore += answerScore

    for (const domain of mapping.domains) {
      switch (domain) {
        case 'P': scores.prolactin += answerScore; break
        case 'A': scores.androgens += answerScore; break
        case 'I': scores.insulin   += answerScore; break
        case 'T': scores.thyroid   += answerScore; break
        case 'mood':  scores.mood  += answerScore; break
        case 'cycle': scores.cycle += answerScore; break
      }
    }
  }

  const signals = {
    prolactin: classifySignal(scores.prolactin, THRESHOLDS.P),
    androgens: classifySignal(scores.androgens, THRESHOLDS.A),
    insulin:   classifySignal(scores.insulin,   THRESHOLDS.I),
    thyroid:   classifySignal(scores.thyroid,   THRESHOLDS.T),
  }

  const investigationGap = calculateInvestigationGap(answers)

  return { totalScore, ...scores, signals, investigationGap }
}

function classifySignal(
  score: number,
  threshold: { signal: number; strong: number }
): SignalLevel {
  if (score >= threshold.strong) return 'strongly-signalled'
  if (score >= threshold.signal) return 'signalled'
  return 'clear'
}

function calculateInvestigationGap(answers: Answer[]): string[] {
  const q21 = answers.find(a => a.questionId === 'Q21')

  const testMap: Record<string, string> = {
    'fsh-lh':             'FSH/LH',
    'prolactin':          'Prolactin',
    'tsh-t4':             'TSH/T4',
    'testosterone-dheas': 'Testosterone/DHEAS',
    'ogtt-i':             'OGTT-I (Insulin Resistance)',
    'progesterone':       'Progesterone (Day 21)',
  }

  if (!q21) return Object.values(testMap)

  const selectedKeys = q21.answerKey.split(',').map(k => k.trim())

  if (selectedKeys.includes('none') || selectedKeys.includes('dont-know')) {
    return Object.values(testMap)
  }

  return Object.entries(testMap)
    .filter(([key]) => !selectedKeys.includes(key))
    .map(([, name]) => name)
}
